<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edFileUrls: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      existingFiles: [...(this.edFileUrls || [])],
      newFiles: [],
      removedFiles: [],
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
  },
  methods: {
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.newFiles = [];
      this.removedFiles = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    onFileSelect(e) {
      const selected = Array.from(e.target.files || []);
      this.newFiles.push(...selected);
    },
    removeNewFile(idx) {
      this.newFiles.splice(idx, 1);
    },
    removeExistingFile(file) {
      this.existingFiles = this.existingFiles.filter(f => f.filename !== file.filename);
      this.removedFiles.push(file.filename);
    },
    formatSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1048576) return (bytes / 1024).toFixed(0) + ' KB';
      return (bytes / 1048576).toFixed(1) + ' MB';
    },
    editCannedResponse() {
      this.editCanned.showLoading = true;

      const formData = new FormData();
      formData.append('canned_response[short_code]', this.shortCode);
      formData.append('canned_response[content]', this.content);
      this.newFiles.forEach(f => formData.append('canned_response[files][]', f));
      this.removedFiles.forEach(f => formData.append('canned_response[remove_files][]', f));

      const accountId = this.$store.getters.getCurrentAccountId;
      const token = this.$store.getters.getCurrentUser?.access_token;

      fetch(`/api/v1/accounts/${accountId}/canned_responses/${this.id}`, {
        method: 'PATCH',
        headers: { api_access_token: token },
        body: formData,
      })
        .then(r => {
          if (!r.ok) throw new Error('Erro ao atualizar');
          return r.json();
        })
        .then(() => {
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.$store.dispatch('getCannedResponse');
          this.onClose();
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>

        <!-- Anexos existentes -->
        <div v-if="existingFiles.length > 0" class="w-full mt-2">
          <label class="text-sm font-medium text-n-slate-11">Anexos atuais</label>
          <div class="mt-1 flex flex-wrap gap-2">
            <div
              v-for="file in existingFiles"
              :key="file.filename"
              class="flex items-center gap-2 px-2 py-1 bg-n-slate-2 rounded-lg text-xs"
            >
              <span>{{ file.filename }}</span>
              <button
                type="button"
                class="text-red-500 hover:text-red-700"
                @click="removeExistingFile(file)"
              >✕</button>
            </div>
          </div>
        </div>

        <!-- Novos anexos -->
        <div class="w-full mt-2">
          <div class="flex flex-wrap gap-2">
            <div
              v-for="(file, idx) in newFiles"
              :key="'new-' + idx"
              class="flex items-center gap-2 px-2 py-1 bg-n-slate-2 rounded-lg text-xs"
            >
              <span>{{ file.name }}</span>
              <span class="text-n-slate-8">{{ formatSize(file.size) }}</span>
              <button
                type="button"
                class="text-red-500 hover:text-red-700"
                @click="removeNewFile(idx)"
              >✕</button>
            </div>
          </div>
          <label
            class="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 border border-dashed border-n-weak rounded-lg text-xs text-n-slate-11 cursor-pointer hover:bg-n-alpha-1 transition-colors"
          >
            📎 Adicionar arquivo
            <input
              type="file"
              class="hidden"
              accept="image/*,audio/*,video/*,.pdf,.doc,.docx"
              multiple
              @change="onFileSelect"
            />
          </label>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];
}
</style>
